import React, { useEffect, useRef } from 'react';

/**
 * BoticarIA Orb — orbe de energía WebGL (sin dependencias).
 * Paleta La Botica: coral #D97E7E · celeste #7FA3B8 · blanco cálido #F2F1F0
 * Estados: idle | typing | thinking | speaking
 */

const VERT = `
attribute vec2 a_pos;
void main() {
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

const FRAG = `
precision highp float;

uniform vec2  u_res;
uniform float u_time;
uniform float u_amp;
uniform float u_speed;
uniform float u_glow;
uniform float u_pulse;
uniform float u_wave;
uniform float u_alpha;
uniform vec2  u_mouse;
uniform float u_mstr;
uniform float u_burst;

float hash(vec3 p) {
  p = fract(p * 0.3183099 + 0.1);
  p *= 17.0;
  return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
}

float noise(vec3 x) {
  vec3 i = floor(x);
  vec3 f = fract(x);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(mix(hash(i + vec3(0.0, 0.0, 0.0)), hash(i + vec3(1.0, 0.0, 0.0)), f.x),
        mix(hash(i + vec3(0.0, 1.0, 0.0)), hash(i + vec3(1.0, 1.0, 0.0)), f.x), f.y),
    mix(mix(hash(i + vec3(0.0, 0.0, 1.0)), hash(i + vec3(1.0, 0.0, 1.0)), f.x),
        mix(hash(i + vec3(0.0, 1.0, 1.0)), hash(i + vec3(1.0, 1.0, 1.0)), f.x), f.y),
    f.z);
}

float fbm(vec3 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p = p * 2.03 + vec3(11.7, 5.3, 9.1);
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 uv = (gl_FragCoord.xy * 2.0 - u_res) / min(u_res.x, u_res.y);
  float r = length(uv);
  float ang = atan(uv.y, uv.x);
  float t = u_time * u_speed;

  float n1 = fbm(vec3(uv * 1.7, t * 0.35));
  float n2 = fbm(vec3(uv * 3.6 + 7.0, t * 0.6));

  float radius = 0.44 + u_pulse * 0.03 * sin(t * 7.5);
  float edge = radius + (n1 - 0.5) * u_amp * 0.6 + (n2 - 0.5) * u_amp * 0.24;
  float md = length(uv - u_mouse);
  edge += u_mstr * 0.07 * exp(-md * md * 5.0);
  float d = r - edge;

  float body = smoothstep(0.02, -0.08, d);
  float rim  = exp(-abs(d) * 20.0);
  float core = exp(-r * r * 11.0) * (0.55 + 0.45 * n2);

  float rings = 0.0;
  if (u_wave > 0.001 && d > 0.0) {
    rings = u_wave * exp(-d * 3.2) * (0.5 + 0.5 * sin((r - t * 0.4) * 42.0));
    rings *= 1.0 - smoothstep(0.68, 0.96, r);
  }
  if (u_burst > 0.001 && d > 0.0) {
    float b = u_burst * exp(-d * 4.5) * (0.5 + 0.5 * sin((r - t * 1.3) * 30.0));
    b *= 1.0 - smoothstep(0.68, 0.96, r);
    rings += b;
  }

  vec3 celeste = vec3(0.498, 0.639, 0.722);
  vec3 coral   = vec3(0.851, 0.494, 0.494);
  vec3 blanco  = vec3(0.949, 0.945, 0.941);

  vec3 bodyCol = mix(celeste, coral, smoothstep(0.3, 0.8, n2) * 0.5);
  vec3 col = bodyCol * body * (0.35 + 0.85 * n1);
  col += coral * rim * (0.75 + 0.35 * sin(ang * 3.0 + t * 1.4));
  col += blanco * core * u_glow * 0.8;
  col += coral * rings * 0.35;

  float alpha = clamp(body * 0.72 + rim * 0.95 + core * u_glow * 0.8 + rings * 0.4, 0.0, 1.0) * u_alpha;
  gl_FragColor = vec4(col * alpha, alpha);
}
`;

const STATES = {
  idle:     { amp: 0.12, speed: 0.32, glow: 0.48, pulse: 0.0,  wave: 0.0  },
  typing:   { amp: 0.20, speed: 0.60, glow: 0.75, pulse: 0.18, wave: 0.0  },
  thinking: { amp: 0.46, speed: 1.55, glow: 0.95, pulse: 0.85, wave: 0.0  },
  speaking: { amp: 0.30, speed: 0.90, glow: 1.00, pulse: 0.30, wave: 0.85 },
};

function compile(gl, type, src) {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    console.error('Orb shader error:', gl.getShaderInfoLog(sh));
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}

export default function Orb({ state = 'idle', docked = false, className = '' }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = STATES[state] ? state : 'idle';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: true,
      premultipliedAlpha: true,
      powerPreference: 'low-power',
    });
    if (!gl) return; // sin WebGL, el halo CSS de respaldo queda visible

    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;

    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.error('Orb link error:', gl.getProgramInfoLog(prog));
      return;
    }
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.clearColor(0, 0, 0, 0);

    const U = {};
    ['u_res', 'u_time', 'u_amp', 'u_speed', 'u_glow', 'u_pulse', 'u_wave', 'u_alpha', 'u_mouse', 'u_mstr', 'u_burst']
      .forEach((n) => { U[n] = gl.getUniformLocation(prog, n); });

    const reduced = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const cur = { ...STATES.idle, alpha: 0 };
    const mouse = { x: 0, y: 0, tx: 0, ty: 0, str: 0, tstr: 0, burst: 0 };
    let raf = 0;
    let running = true;
    const t0 = performance.now();

    const toOrbUv = (e) => {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width) return null;
      return {
        x: ((e.clientX - rect.left) / rect.width) * 2 - 1,
        y: -(((e.clientY - rect.top) / rect.height) * 2 - 1),
      };
    };

    const onMove = (e) => {
      if (reduced) return;
      const p = toOrbUv(e);
      if (!p) return;
      const dist = Math.hypot(p.x, p.y);
      mouse.tx = p.x;
      mouse.ty = p.y;
      mouse.tstr = dist < 1.5 ? Math.max(0, 1 - dist / 1.5) : 0;
    };

    const onDown = (e) => {
      if (reduced) return;
      const p = toOrbUv(e);
      if (!p) return;
      if (Math.hypot(p.x, p.y) < 1.2) {
        mouse.burst = 1;
      }
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerdown', onDown, { passive: true });

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const w = Math.round(canvas.clientWidth * dpr);
      const h = Math.round(canvas.clientHeight * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    };

    const frame = (now) => {
      if (!running) return;
      resize();

      const target = STATES[stateRef.current];
      const k = 0.045;
      cur.amp   += (target.amp   - cur.amp)   * k;
      cur.speed += ((reduced ? target.speed * 0.15 : target.speed) - cur.speed) * k;
      cur.glow  += (target.glow  - cur.glow)  * k;
      cur.pulse += ((reduced ? 0 : target.pulse) - cur.pulse) * k;
      cur.wave  += ((reduced ? 0 : target.wave)  - cur.wave)  * k;
      cur.alpha += (1 - cur.alpha) * 0.02;

      mouse.x += (mouse.tx - mouse.x) * 0.08;
      mouse.y += (mouse.ty - mouse.y) * 0.08;
      mouse.str += (mouse.tstr - mouse.str) * 0.06;
      mouse.burst *= 0.965;

      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform2f(U.u_res, canvas.width, canvas.height);
      gl.uniform1f(U.u_time, (now - t0) / 1000);
      gl.uniform1f(U.u_amp, cur.amp);
      gl.uniform1f(U.u_speed, cur.speed);
      gl.uniform1f(U.u_glow, cur.glow);
      gl.uniform1f(U.u_pulse, cur.pulse);
      gl.uniform1f(U.u_wave, cur.wave);
      gl.uniform1f(U.u_alpha, cur.alpha);
      gl.uniform2f(U.u_mouse, mouse.x, mouse.y);
      gl.uniform1f(U.u_mstr, mouse.str);
      gl.uniform1f(U.u_burst, mouse.burst < 0.01 ? 0 : mouse.burst);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      raf = requestAnimationFrame(frame);
    };

    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        raf = requestAnimationFrame(frame);
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    raf = requestAnimationFrame(frame);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onDown);
      gl.deleteProgram(prog);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      gl.deleteBuffer(buf);
    };
  }, []);

  return (
    <div className={`orb-stage ${docked ? 'orb-docked' : ''} ${className}`} aria-hidden="true">
      <div className="orb-halo" />
      <canvas ref={canvasRef} className="orb-canvas" />
    </div>
  );
}
